import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Grid, ButtonGroup, Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CategoryIcon from '@mui/icons-material/Category';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LinkIcon from '@mui/icons-material/Link';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cardManagementService } from '../../services/cardManagementService';
import { clubEntryService } from '../../services/clubEntryService';
import type { CardEntryRightsDto } from '../../services/clubEntryService';
import AddCardModal from './modals/AddCardModal';
import BulkAddCardModal from './modals/BulkAddCardModal';
import CardPairingModal from './modals/CardPairingModal';
import SessionDetailModal from './components/SessionDetailModal';
import GuideButton from '../common/guide/GuideButton';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../accounting/shared/StatsCard';
import { accountingApi } from '../../config/apiClient';
import { getPersonnel as getPersonnelSql } from '../../services/migration/personnelDataService';
import { useTranslation } from 'react-i18next';

const getContrastTextColor = (hexColor: string): string => {
  const hex = hexColor.replace('#', '');
  if (hex.length < 6) return '#000';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000' : '#fff';
};

// CardType CRUD Modal
const CardTypeModal: React.FC<{ open: boolean; onClose: () => void; companyId: string; onSuccess: () => void }> = ({ open, onClose, companyId, onSuccess }) => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { currentUser } = useAuth();
  const [cardTypes, setCardTypes] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#1a237e');
  const [depositAmount, setDepositAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState<{ [id: number]: string }>({});
  const [depositSaved, setDepositSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await cardManagementService.getCardTypes(companyId);
      setCardTypes(res.data?.data || []);
    } catch { }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleAdd = async () => {
    if (!name) return;
    setLoading(true);
    try {
      await cardManagementService.createCardType({
        Name: name,
        Description: description,
        Color: color,
        DepositAmount: parseFloat(depositAmount) || 0,
        CreatedBy: currentUser?.email,
      });
      setName(''); setDescription(''); setDepositAmount(''); load(); onSuccess();
    } catch { }
    setLoading(false);
  };

  const handleDepositBlur = async (ct: any) => {
    const newVal = parseFloat(editingDeposit[ct.id] ?? '');
    if (isNaN(newVal) || newVal === (ct.depositAmount ?? 0)) {
      setEditingDeposit(prev => { const n = { ...prev }; delete n[ct.id]; return n; });
      return;
    }
    try {
      await cardManagementService.updateCardType({ Id: ct.id, DepositAmount: newVal });
      setDepositSaved(`${ct.name}: ₺${newVal.toFixed(2)} ${cm('cardTypeModal.saved', 'saved')}`);
      load(); onSuccess();
    } catch { }
    setEditingDeposit(prev => { const n = { ...prev }; delete n[ct.id]; return n; });
  };

  const handleDelete = async (id: number) => {
    try {
      await cardManagementService.deleteCardType({ Id: id, DeletedBy: currentUser?.email });
      load(); onSuccess();
    } catch { }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{cm('cardTypeModal.title', 'Card Type Management')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField label={cm('cardTypeModal.fields.typeName', 'Type Name')} value={name} onChange={(e) => setName(e.target.value)} size="small" sx={{ flex: 1, minWidth: 120 }} />
          <TextField label={cm('cardTypeModal.fields.color', 'Color')} type="color" value={color} onChange={(e) => setColor(e.target.value)} size="small" sx={{ width: 80 }} />
          <TextField
            label={cm('cardTypeModal.fields.deposit', 'Deposit (₺)')}
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            size="small"
            sx={{ width: 110 }}
            placeholder="0"
          />
          <Button onClick={handleAdd} variant="contained" disabled={loading} size="small">{cm('cardTypeModal.add', 'Add')}</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{cm('cardTypeModal.columns.name', 'Name')}</TableCell>
                <TableCell>{cm('cardTypeModal.columns.color', 'Color')}</TableCell>
                <TableCell align="right">{cm('cardTypeModal.columns.deposit', 'Deposit (₺)')}</TableCell>
                <TableCell width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {cardTypes.map((ct) => (
                <TableRow key={ct.id}>
                  <TableCell>{ct.name}</TableCell>
                  <TableCell><Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: ct.color || '#ccc' }} /></TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={editingDeposit[ct.id] !== undefined ? editingDeposit[ct.id] : (ct.depositAmount ?? 0)}
                      onChange={(e) => setEditingDeposit(prev => ({ ...prev, [ct.id]: e.target.value }))}
                      onBlur={() => handleDepositBlur(ct)}
                      sx={{ width: 100 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell><IconButton size="small" onClick={() => handleDelete(ct.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{cm('common.close', 'Close')}</Button></DialogActions>
      <Snackbar open={!!depositSaved} autoHideDuration={2500} onClose={() => setDepositSaved(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setDepositSaved(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{depositSaved}</Alert>
      </Snackbar>
    </Dialog>
  );
};

// Wallet CRUD Modal
const WalletModal: React.FC<{ open: boolean; onClose: () => void; companyId: string; onSuccess: () => void }> = ({ open, onClose, companyId, onSuccess }) => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { currentUser } = useAuth();
  const [walletsList, setWalletsList] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await cardManagementService.getWallets(companyId);
      setWalletsList(res.data?.data || []);
    } catch { }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleAdd = async () => {
    if (!name) return;
    setLoading(true);
    try {
      await cardManagementService.createWallet({ Name: name, Currency: currency, CreatedBy: currentUser?.email });
      setName(''); load(); onSuccess();
    } catch { }
    setLoading(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await cardManagementService.deleteWallet({ Id: id, DeletedBy: currentUser?.email });
      load(); onSuccess();
    } catch { }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{cm('walletModal.title', 'Wallet Management')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
          <TextField label={cm('walletModal.fields.walletName', 'Wallet Name')} value={name} onChange={(e) => setName(e.target.value)} size="small" sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ width: 100 }}>
            <InputLabel>{cm('walletModal.fields.currency', 'Currency')}</InputLabel>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} label={cm('walletModal.fields.currency', 'Currency')}>
              <MenuItem value="TRY">TRY</MenuItem><MenuItem value="USD">USD</MenuItem><MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </FormControl>
          <Button onClick={handleAdd} variant="contained" disabled={loading} size="small">{cm('walletModal.add', 'Add')}</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>{cm('walletModal.columns.name', 'Name')}</TableCell><TableCell>{cm('walletModal.columns.currency', 'Currency')}</TableCell><TableCell width={60} /></TableRow></TableHead>
            <TableBody>
              {walletsList.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{w.name}</TableCell>
                  <TableCell>{w.currency}</TableCell>
                  <TableCell><IconButton size="small" onClick={() => handleDelete(w.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{cm('common.close', 'Close')}</Button></DialogActions>
    </Dialog>
  );
};

const Cards: React.FC = () => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { currentUser, userDetails } = useAuth();
  const companyId = userDetails?.companyId || '';
  const navigate = useNavigate();

  const [cards, setCards] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState<any>({ totalCards: 0, cardTypeCount: 0, totalBalance: 0, walletCount: 0 });

  const [cardTypes, setCardTypes] = useState<any[]>([]);
  const [wallets, setWallets] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [personnel, setPersonnel] = useState<any[]>([]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editCardData, setEditCardData] = useState<any>(null);
  const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);
  const [cardTypeModalOpen, setCardTypeModalOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [pairingModalOpen, setPairingModalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  const [anchorElRow, setAnchorElRow] = useState<{ [key: string]: HTMLElement | null }>({});

  const [sessionStatusFilter, setSessionStatusFilter] = useState<string>('');
  const [cardTypeFilter, setCardTypeFilter] = useState<string>('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteCheckLoading, setDeleteCheckLoading] = useState(false);
  const [hasTransactions, setHasTransactions] = useState(false);

  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);
  const [sessionDetailTarget, setSessionDetailTarget] = useState<any>(null);

  const [closeSessionModalOpen, setCloseSessionModalOpen] = useState(false);
  const [closeSessionTarget, setCloseSessionTarget] = useState<any>(null);
  const [closeSessionLoading, setCloseSessionLoading] = useState(false);

  const [openSessionModalOpen, setOpenSessionModalOpen] = useState(false);
  const [openSessionTarget, setOpenSessionTarget] = useState<any>(null);
  const [openSessionLoading, setOpenSessionLoading] = useState(false);

  const [returnCardModalOpen, setReturnCardModalOpen] = useState(false);
  const [returnCardTarget, setReturnCardTarget] = useState<any>(null);
  const [returnCardLoading, setReturnCardLoading] = useState(false);

  // Geçiş Hakkı Tanımla (kulüp girişindeki geçiş hakkı tanımlamanın aynısı, ödemesiz)
  const [entryRightModalOpen, setEntryRightModalOpen] = useState(false);
  const [entryRightTarget, setEntryRightTarget] = useState<any>(null);
  const [entryRightLoading, setEntryRightLoading] = useState(false);
  const [entryRightMode, setEntryRightMode] = useState<string>('SINGLE_ENTRY');
  const [entryRightCount, setEntryRightCount] = useState<string>('1');
  // cardUID -> kalan geçiş hakkı bilgisi (yalnızca oturumu açık kartlar için yüklenir)
  const [entryRightsMap, setEntryRightsMap] = useState<Record<string, CardEntryRightsDto>>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCards = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      console.log('[Cards] 🔍 Kart listesi çekiliyor...', { companyId, page: page + 1, pageSize, search: searchDebounced, sessionStatus: sessionStatusFilter, cardTypeFilter });
      const res = await cardManagementService.getCards(companyId, {
        page: page + 1,
        pageSize,
        search: searchDebounced || undefined,
        sessionStatus: sessionStatusFilter || undefined,
        cardTypeId: cardTypeFilter ? parseInt(cardTypeFilter) : undefined,
      });
      const cards = res.data?.data || [];
      console.log('[Cards] ✅ Gelen kart sayısı:', cards.length, 'Toplam:', res.data?.totalCount);
      if (cards.length > 0) {
        console.log('[Cards] 📋 İlk 3 kart:', cards.slice(0, 3).map((c: any) => ({
          id: c.id, cardUID: c.cardUID, cardNumber: c.cardNumber, companyId: c.companyId, cardTypeName: c.cardTypeName, accountName: c.accountName
        })));
      }
      setCards(cards);
      setTotalCount(res.data?.totalCount || 0);

      // Oturumu açık kartlar için geçiş haklarını toplu çek (yeni "Geçiş Hakkı" sütunu).
      // Oturumu kapalı kartların hakları gösterilmez → onları sorgulamaya gerek yok.
      const activeUids: string[] = cards
        .filter((c: any) => c.activeSession && c.activeSession.status === 'ACTIVE' && c.cardUID)
        .map((c: any) => c.cardUID);
      if (activeUids.length > 0) {
        try {
          const rights = await clubEntryService.getEntryRightsByUids(activeUids);
          const map: Record<string, CardEntryRightsDto> = {};
          for (const r of rights) { if (r?.cardUID) map[r.cardUID] = r; }
          setEntryRightsMap(map);
        } catch {
          setEntryRightsMap({});
        }
      } else {
        setEntryRightsMap({});
      }
    } catch {
      setSnackbar({ open: true, message: cm('cards.messages.loadError', 'An error occurred while loading cards'), severity: 'error' });
    }
    setLoading(false);
  }, [companyId, page, pageSize, searchDebounced, sessionStatusFilter, cardTypeFilter]);

  const loadSummary = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await cardManagementService.getSummary(companyId);
      setSummary(res.data?.data || {});
    } catch { }
  }, [companyId]);

  const loadMeta = useCallback(async () => {
    if (!companyId) return;
    try {
      const [ctRes, wRes] = await Promise.all([
        cardManagementService.getCardTypes(companyId),
        cardManagementService.getWallets(companyId),
      ]);
      setCardTypes(ctRes.data?.data || []);
      setWallets(wRes.data?.data || []);
    } catch { }
    try {
      const accRes = await accountingApi.get(`/api/Account`);
      const accData = accRes.data;
      setAccounts(Array.isArray(accData) ? accData : accData?.data || []);
    } catch { }
    try {
      // Kart sahibi personel de olabildiği için personel listesi de yüklenir.
      const perList = await getPersonnelSql(companyId);
      setPersonnel((perList || [])
        .filter((p: any) => p?.id && !p.isDeleted)
        .map((p: any) => ({ id: p.id, name: p.name || '', email: p.email })));
    } catch { }
  }, [companyId]);

  useEffect(() => { loadCards(); }, [loadCards]);
  useEffect(() => { loadSummary(); loadMeta(); }, [loadSummary, loadMeta]);

  /**
   * Kart sahibinin görüntülenecek adı. Sahip cari ya da personel olabilir; ad
   * snapshot yerine sahip kimliğinden canlı listelerden çözülür (düzenleme
   * modalıyla AYNI kaynak), böylece bayat snapshot doğru adı gizlemez.
   */
  const resolveCardOwnerName = useCallback((row: any): string => {
    if (row?.ownerType === 'PERSONNEL') {
      const matched = personnel.find((p: any) => String(p.id) === String(row.personnelFirebaseUid));
      return matched?.name || row.personnelName || '';
    }
    const matched = accounts.find((a: any) => String(a.id) === String(row?.accountId));
    const assigned = matched?.companyName || row?.accountName;
    if (assigned) return assigned;
    return row?.activeSession?.status === 'ACTIVE' ? (row.activeSession.accountName || '') : '';
  }, [accounts, personnel]);

  const openDeleteModal = async (card: any) => {
    setDeleteTarget(card);
    setDeleteCheckLoading(true);
    setHasTransactions(false);
    setDeleteModalOpen(true);
    try {
      const res = await cardManagementService.getCardTransactions(card.id, companyId, { page: 1, pageSize: 1 });
      setHasTransactions((res.data?.totalCount || 0) > 0);
    } catch {
      setHasTransactions(true);
    }
    setDeleteCheckLoading(false);
  };

  const handleDeleteCard = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await cardManagementService.deleteCard({ Id: deleteTarget.id, DeletedBy: currentUser?.email });
      setSnackbar({ open: true, message: cm('cards.messages.deleted', 'Card deleted'), severity: 'success' });
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      loadCards();
      loadSummary();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || cm('cards.messages.deleteError', 'An error occurred while deleting card'), severity: 'error' });
    }
    setDeleteLoading(false);
  };

  const handleCardCreated = () => {
    setSnackbar({ open: true, message: cm('cards.messages.created', 'Card created successfully'), severity: 'success' });
    loadCards();
    loadSummary();
    loadMeta();
  };

  const handleCloseSession = async () => {
    if (!closeSessionTarget?.activeSession?.id) return;
    setCloseSessionLoading(true);
    try {
      await cardManagementService.closeSession({
        SessionId: closeSessionTarget.activeSession.id,
        UserId: currentUser?.email || 'admin',
      });

      // ✅ Oturum kapatılınca kulüp girişi aktif kart kaydını da kaldır (üye girişleri
      //    listesinden düşsün). returnCard kaydı IsReturned=true + Status=RETURNED yapar,
      //    böylece "Üye Girişleri" modalında artık görünmez. Kayıt yoksa sessizce geç.
      if (closeSessionTarget.cardUID) {
        try {
          await clubEntryService.returnCard(closeSessionTarget.cardUID, currentUser?.email || 'kart-yonetimi');
        } catch { /* aktif kulüp girişi kaydı yok → yalnızca oturum kapatıldı */ }
      }

      setSnackbar({ open: true, message: cm('cards.messages.sessionClosed', 'Session closed successfully'), severity: 'success' });
      setCloseSessionModalOpen(false);
      setCloseSessionTarget(null);
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || cm('cards.messages.sessionCloseError', 'An error occurred while closing session'), severity: 'error' });
    }
    setCloseSessionLoading(false);
  };

  // Oturumu Başlat: kulüp girişindeki openSession ile birebir aynı (accounting kart oturumu).
  const handleOpenSession = async () => {
    if (!openSessionTarget?.id) return;
    setOpenSessionLoading(true);
    try {
      // ✅ Oturuma kartın ATANMIŞ carisini yaz (geçmiş oturumlar sayfası bunu gösterir).
      //    Doğru adı accounts listesinden çöz (accountName bayat olabilir, örn. eski "Genel Cari").
      //    NOT: OpenSessionAsync aynı zamanda kartın AccountId/AccountName'ini dto ile günceller;
      //    bu yüzden kartın kendi carisini geçmek kart verisini bozmaz (no-op olur).
      //    Kart PERSONELE bağlıysa backend bu alanları yok sayar ve oturum kartın
      //    sahibini devralır; yine de gönderiyoruz ki uç tek imzayla çalışsın.
      const matchedAccount = accounts.find((a: any) => String(a.id) === String(openSessionTarget.accountId));
      const sessionAccountId = openSessionTarget.accountId || '0';
      const sessionAccountName = matchedAccount?.companyName || openSessionTarget.accountName || 'Kulüp Girişi';

      await cardManagementService.openSession({
        CardId: openSessionTarget.id,
        AccountId: sessionAccountId,
        AccountName: sessionAccountName,
        UserId: currentUser?.email || 'admin',
        ScreenId: '',
      });

      // ✅ Kartı "müşteriye verilmiş gibi" kulüp girişi aktif kart kaydı olarak da işle.
      //    Accounting oturumu açmak tek başına yeterli DEĞİL; kulüp girişi "Üye Girişleri"
      //    modalı ClubEntrySaleCards tablosunu listeler. Bu yüzden ekranı belirsiz (NULL)
      //    bir ClubEntrySaleCard oluşturuyoruz (grant-entry-rights) → tüm ekranların üye
      //    girişlerinde görünür. Geçiş hakkı sayısı sonradan "Geçiş Hakkı Tanımla" ile
      //    değiştirilebilir (varsayılan: 1 tek giriş).
      let memberEntryWarning = false;
      if (openSessionTarget.cardUID) {
        try {
          await clubEntryService.grantEntryRights({
            cardUID: openSessionTarget.cardUID,
            cardLabel: resolveCardOwnerName(openSessionTarget) || openSessionTarget.cardNumber,
            entryMode: 'SINGLE_ENTRY',
            totalEntryRights: 1,
            createdBy: currentUser?.email || 'kart-yonetimi',
          });
        } catch {
          // Oturum açıldı; kulüp girişi kaydı başarısız olsa da akışı bozma.
          memberEntryWarning = true;
        }
      } else {
        // CardUID yoksa kulüp girişinde (turnike) kart eşleşemez.
        memberEntryWarning = true;
      }

      if (memberEntryWarning) {
        setSnackbar({ open: true, message: tr('Oturum açıldı, ancak üye girişi kaydı oluşturulamadı (kart UID eksik veya hata).', 'Session started, but member-entry record could not be created (missing card UID or error).'), severity: 'error' });
      } else {
        setSnackbar({ open: true, message: cm('cards.messages.sessionOpened', 'Session started successfully'), severity: 'success' });
      }
      setOpenSessionModalOpen(false);
      setOpenSessionTarget(null);
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || cm('cards.messages.sessionOpenError', 'An error occurred while starting the session'), severity: 'error' });
    }
    setOpenSessionLoading(false);
  };

  // Kartı Teslim Al: kulüp girişi kartını teslim al + (varsa) accounting oturumunu kapat.
  // Fark: depozito SORULMADAN, ödenmiş/kapatılmış kabul edilerek "iade edildi" işaretlenir
  // (muhasebe para hareketi YOK). Yönetici, kulüp girişi ekranından teslim alınamayan
  // kartı bu sayfadan doğrudan teslim alabilir.
  const handleReturnCard = async () => {
    const row = returnCardTarget;
    if (!row) return;
    setReturnCardLoading(true);
    const messages: string[] = [];
    let anySuccess = false;
    try {
      if (row.cardUID) {
        try {
          const lookup = await clubEntryService.lookupCard(row.cardUID);
          const match = [lookup.card, ...(lookup.relatedCards || [])]
            .find((c) => c.cardUID === row.cardUID && !c.isReturned && (c.status === 'ACTIVE' || c.status === 'EXPIRED'));
          if (match) {
            await clubEntryService.returnCard(row.cardUID, currentUser?.email || 'kart-yonetimi');
            anySuccess = true;
            messages.push(tr('Kart teslim alındı', 'Card returned'));
            if (match.depositAmount > 0 && !match.depositRefunded) {
              try {
                await clubEntryService.markDepositRefunded(row.cardUID);
                messages.push(tr(`₺${match.depositAmount.toFixed(2)} depozito kapatıldı`, `₺${match.depositAmount.toFixed(2)} deposit settled`));
              } catch { /* depozito işaretleme başarısız olsa da teslim alma geçerli */ }
            }
          }
        } catch { /* kulüp girişi kaydı yok → yalnızca oturum kapatma yapılacak */ }
      }

      if (row.activeSession && row.activeSession.status === 'ACTIVE' && row.activeSession.id) {
        try {
          await cardManagementService.closeSession({
            SessionId: row.activeSession.id,
            UserId: currentUser?.email || 'admin',
          });
          anySuccess = true;
          messages.push(tr('Oturum kapatıldı', 'Session closed'));
        } catch { /* oturum zaten kapalı olabilir */ }
      }

      if (anySuccess) {
        setSnackbar({ open: true, message: messages.join(' • '), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: tr('Bu kart için teslim alınacak aktif kayıt/oturum bulunamadı.', 'No active record/session to return for this card.'), severity: 'error' });
      }
      setReturnCardModalOpen(false);
      setReturnCardTarget(null);
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || tr('Kart teslim alma sırasında bir hata oluştu.', 'An error occurred while returning the card.'), severity: 'error' });
    }
    setReturnCardLoading(false);
  };

  // Geçiş Hakkı Tanımla: kulüp girişi hızlı satış ekranındaki geçiş hakkı tanımlamanın
  // ödemesiz karşılığı. Kart, kulüp girişinde tanımlanmış gibi turnikeden geçebilir.
  const handleGrantEntryRights = async () => {
    const row = entryRightTarget;
    if (!row?.cardUID) return;
    const total = parseInt(entryRightCount, 10);
    if (entryRightMode === 'SINGLE_ENTRY' && (!total || total < 1)) {
      setSnackbar({ open: true, message: tr('Geçerli bir geçiş hakkı sayısı girin', 'Enter a valid number of entry rights'), severity: 'error' });
      return;
    }
    setEntryRightLoading(true);
    try {
      await clubEntryService.grantEntryRights({
        cardUID: row.cardUID,
        cardLabel: resolveCardOwnerName(row) || row.cardNumber,
        entryMode: entryRightMode,
        totalEntryRights: entryRightMode === 'UNLIMITED_DAILY' ? Math.max(total || 1, 1) : total,
        createdBy: currentUser?.email || 'kart-yonetimi',
      });
      setSnackbar({ open: true, message: tr('Geçiş hakkı tanımlandı', 'Entry rights defined'), severity: 'success' });
      setEntryRightModalOpen(false);
      setEntryRightTarget(null);
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || tr('Geçiş hakkı tanımlanırken bir hata oluştu', 'An error occurred while defining entry rights'), severity: 'error' });
    }
    setEntryRightLoading(false);
  };

  const handleToggleMaster = async (row: any) => {
    const newValue = !row.isMasterCard;
    try {
      await cardManagementService.updateCard({ Id: row.id, IsMasterCard: newValue });
      setSnackbar({ open: true, message: newValue ? cm('cards.messages.masterSet', 'Card marked as master') : cm('cards.messages.masterUnset', 'Master flag removed'), severity: 'success' });
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || cm('cards.messages.masterError', 'Master card operation failed'), severity: 'error' });
    }
  };

  // AccountingDataTable kolon tanımları
  const cardColumns = useMemo<DataTableColumn<any>[]>(() => [
    {
      id: 'rowNumber',
      label: cm('cards.table.no', 'No'),
      width: '60px',
      render: (_row: any, index: number) => (
        <Box component="span" sx={{ color: _row.isMasterCard ? 'warning.main' : 'text.secondary', fontWeight: _row.isMasterCard ? 'bold' : undefined }}>
          {page * pageSize + index + 1}
        </Box>
      ),
    },
    {
      id: 'cardNumber',
      label: cm('cards.table.cardNo', 'Card No'),
      sortable: true,
      render: (row: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.isMasterCard && <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: row.isMasterCard ? 'warning.main' : undefined, fontWeight: row.isMasterCard ? 'bold' : undefined }}>
            {row.cardNumber}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'cardUID',
      label: cm('cards.table.cardUid', 'Card UID'),
      sortable: true,
      render: (row: any) => (
        <Typography variant="body2" sx={{ color: row.isMasterCard ? 'warning.main' : undefined, fontWeight: row.isMasterCard ? 'bold' : undefined }}>
          {row.cardUID || '-'}
        </Typography>
      ),
    },
    {
      id: 'turnstileCardUID',
      label: cm('cards.table.turnstileUid', isEnglish ? 'Turnstile UID' : 'Turnike UID'),
      render: (row: any) => (
        row.turnstileCardUID ? (
          <Chip
            label={row.turnstileCardUID}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22 }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>
        )
      ),
    },
    {
      id: 'cardType',
      label: cm('cards.table.cardType', 'Card Type'),
      sortable: true,
      render: (row: any) => {
        const bg = row.cardTypeColor || '#ccc';
        return (
          <Chip label={row.cardTypeName || '-'} size="small"
            sx={{ bgcolor: bg, color: getContrastTextColor(bg), fontWeight: 500 }} />
        );
      },
    },
    {
      id: 'wallets',
      label: cm('cards.table.wallets', 'Wallets'),
      render: (row: any) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(row.wallets || []).map((w: any) => (
            <Chip key={w.walletId} label={w.walletName} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      id: 'accountName',
      label: cm('cards.table.owner', 'Card Owner'),
      sortable: true,
      render: (row: any) => {
        const isPersonnelOwner = row.ownerType === 'PERSONNEL';
        const name = resolveCardOwnerName(row);

        if (!name) {
          return <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>;
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <span>{name}</span>
            <Chip
              label={isPersonnelOwner
                ? cm('cards.table.ownerPersonnel', 'Personnel')
                : cm('cards.table.ownerAccount', 'Account')}
              size="small"
              variant="outlined"
              color={isPersonnelOwner ? 'secondary' : 'default'}
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Box>
        );
      },
    },
    {
      id: 'totalBalance',
      label: cm('cards.table.totalBalance', 'Total Balance'),
      align: 'right',
      sortable: true,
      render: (row: any) => {
        const bal = row.totalBalance || 0;
        return (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: bal >= 0 ? 'success.main' : 'error.main' }}>
            {bal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
          </Typography>
        );
      },
    },
    {
      id: 'sessionStatus',
      label: cm('cards.table.session', 'Session'),
      render: (row: any) => {
        const hasActiveSession = row.activeSession && row.activeSession.status === 'ACTIVE';
        return (
          <Chip
            label={hasActiveSession ? cm('cards.table.open', 'Open') : cm('cards.table.closed', 'Closed')}
            size="small"
            color={hasActiveSession ? 'info' : 'default'}
            variant={hasActiveSession ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      id: 'entryRights',
      label: cm('cards.table.entryRights', 'Entry Rights'),
      render: (row: any) => {
        const hasActiveSession = row.activeSession && row.activeSession.status === 'ACTIVE';
        // Oturumu kapalı kartların geçiş hakları gösterilmez.
        if (!hasActiveSession) {
          return <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>;
        }
        const r = entryRightsMap[row.cardUID];
        // Hücreye tıklayınca tanımlama/düzenleme modalı (mevcut değerlerle) açılır.
        const openEdit = (e: React.MouseEvent) => {
          e.stopPropagation();
          setEntryRightTarget(row);
          setEntryRightMode(r?.entryMode || 'SINGLE_ENTRY');
          setEntryRightCount(r && r.entryMode === 'SINGLE_ENTRY' ? String(r.totalEntryRights) : '1');
          setEntryRightModalOpen(true);
        };
        if (!r) {
          return (
            <Chip onClick={openEdit} label={cm('cards.table.entryRightsNone', 'Not defined')} size="small" variant="outlined" color="default" sx={{ cursor: 'pointer' }} />
          );
        }
        if (r.entryMode === 'UNLIMITED_DAILY') {
          return (
            <Chip onClick={openEdit} icon={<MeetingRoomIcon />} label={cm('cards.table.entryRightsUnlimited', 'Unlimited (Daily)')} size="small" color="success" variant="filled" sx={{ cursor: 'pointer' }} />
          );
        }
        const remaining = r.remainingEntryRights;
        return (
          <Chip
            onClick={openEdit}
            icon={<MeetingRoomIcon />}
            label={`${remaining}/${r.totalEntryRights} ${cm('cards.table.entryRightsUnit', 'entries')}`}
            size="small"
            color={remaining > 0 ? 'success' : 'default'}
            variant={remaining > 0 ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        );
      },
    },
    {
      id: 'status',
      label: cm('cards.table.status', 'Status'),
      render: (row: any) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip label={row.isActive ? cm('cards.table.active', 'Active') : cm('cards.table.inactive', 'Inactive')} size="small"
            color={row.isActive ? 'success' : 'default'} />
          {row.isMasterCard && (
            <Chip label="Master" size="small" color="warning" icon={<StarIcon />} />
          )}
        </Box>
      ),
    },
    {
      id: 'actions',
      label: cm('cards.table.actions', 'Actions'),
      align: 'right',
      render: (row: any) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow(prev => ({ ...prev, [row.id]: e.currentTarget })); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => setAnchorElRow(prev => ({ ...prev, [row.id]: null }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [row.id]: null })); setEditCardData(row); setAddModalOpen(true); }}>
              <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={cm('cards.actions.edit', 'Edit')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [row.id]: null })); openDeleteModal(row); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={cm('cards.actions.delete', 'Delete')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [row.id]: null })); navigate(`/kart-yonetimi/oturumlar?cardNumber=${encodeURIComponent(row.cardNumber || '')}`); }}>
              <ListItemIcon><HistoryIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={cm('cards.actions.pastSessions', 'Past Sessions')} />
            </MenuItem>
            <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [row.id]: null })); handleToggleMaster(row); }}>
              <ListItemIcon>{row.isMasterCard ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={row.isMasterCard ? cm('cards.actions.unsetMaster', 'Unset Master') : cm('cards.actions.setMaster', 'Set Master')} sx={row.isMasterCard ? { color: 'warning.main' } : undefined} />
            </MenuItem>
            {!(row.activeSession && row.activeSession.status === 'ACTIVE') && (
              <MenuItem onClick={() => {
                setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
                setOpenSessionTarget(row);
                setOpenSessionModalOpen(true);
              }}>
                <ListItemIcon><PlayCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText primary={cm('cards.actions.openSession', 'Start Session')} sx={{ color: 'success.main' }} />
              </MenuItem>
            )}
            {row.activeSession && row.activeSession.status === 'ACTIVE' && (
              <MenuItem onClick={() => {
                setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
                setSessionDetailTarget(row.activeSession);
                setSessionDetailOpen(true);
              }}>
                <ListItemIcon><InfoOutlinedIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText primary={cm('cards.actions.sessionDetails', 'Session Details')} />
              </MenuItem>
            )}
            {row.activeSession && row.activeSession.status === 'ACTIVE' && (
              <MenuItem onClick={() => {
                setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
                setReturnCardTarget(row);
                setReturnCardModalOpen(true);
              }}>
                <ListItemIcon><AssignmentReturnIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary={cm('cards.actions.returnCard', 'Return Card')} sx={{ color: 'secondary.main' }} />
              </MenuItem>
            )}
            {row.activeSession && row.activeSession.status === 'ACTIVE' && (
              <MenuItem onClick={() => {
                setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
                const existing = entryRightsMap[row.cardUID];
                setEntryRightTarget(row);
                setEntryRightMode(existing?.entryMode || 'SINGLE_ENTRY');
                setEntryRightCount(existing && existing.entryMode === 'SINGLE_ENTRY' ? String(existing.totalEntryRights) : '1');
                setEntryRightModalOpen(true);
              }}>
                <ListItemIcon><MeetingRoomIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary={cm('cards.actions.grantEntryRights', 'Define Entry Rights')} sx={{ color: 'primary.main' }} />
              </MenuItem>
            )}
            {row.activeSession && row.activeSession.status === 'ACTIVE' && (
              <MenuItem onClick={() => {
                setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
                setCloseSessionTarget(row);
                setCloseSessionModalOpen(true);
              }}>
                <ListItemIcon><PowerSettingsNewIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary={cm('cards.actions.closeSession', 'Close Session')} sx={{ color: 'warning.main' }} />
              </MenuItem>
            )}
          </Menu>
        </Box>
      ),
    },
  ], [page, pageSize, anchorElRow, entryRightsMap, accounts]);

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CreditCardIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {cm('cards.title', 'Card Definitions')}
          </Typography>
          <GuideButton guideKey="cards.list" sx={{ ml: 0.5 }} />
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && cards.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CreditCardIcon />}
            color={STATS_COLORS.blue}
            title={`${summary.totalCards || 0}`}
            subtitle={cm('cards.stats.totalCards', 'Total Cards')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.purple}
            title={`${summary.cardTypeCount || 0}`}
            subtitle={cm('cards.stats.cardTypes', 'Card Types')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={(summary.totalBalance || 0) >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
            title={formatStatsCurrency(summary.totalBalance || 0)}
            subtitle={cm('cards.stats.totalBalance', 'Total Balance')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AccountBalanceWalletIcon />}
            color={STATS_COLORS.orange}
            title={`${summary.walletCount || 0}`}
            subtitle={cm('cards.stats.walletCount', 'Wallet Count')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              placeholder={cm('cards.filters.searchPlaceholder', 'Search Card UID, Number or Account...')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{cm('cards.filters.sessionStatus', 'Session Status')}</InputLabel>
              <Select
                value={sessionStatusFilter}
                onChange={(e) => { setSessionStatusFilter(e.target.value); setPage(0); }}
                label={cm('cards.filters.sessionStatus', 'Session Status')}
              >
                <MenuItem value="">{cm('cards.filters.all', 'All')}</MenuItem>
                <MenuItem value="ACTIVE">{cm('cards.filters.sessionOpen', 'Session Open')}</MenuItem>
                <MenuItem value="CLOSED">{cm('cards.filters.sessionClosed', 'Session Closed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{cm('cards.filters.cardType', 'Card Type')}</InputLabel>
              <Select
                value={cardTypeFilter}
                onChange={(e) => { setCardTypeFilter(e.target.value); setPage(0); }}
                label={cm('cards.filters.cardType', 'Card Type')}
              >
                <MenuItem value="">{cm('cards.filters.all', 'All')}</MenuItem>
                {cardTypes.map((ct) => (
                  <MenuItem key={ct.id} value={String(ct.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ct.color || '#ccc', flexShrink: 0 }} />
                      {ct.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Desktop Butonlar */}
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)}>
                {cm('cards.buttons.addNewCard', 'Add New Card')}
              </Button>
              <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => setPairingModalOpen(true)}>
                {cm('cards.buttons.cardPairing', isEnglish ? 'Card Pairing' : 'Kart Eşleştirme')}
              </Button>
              <Button variant="outlined" onClick={(e) => setAnchorElMore(e.currentTarget)} sx={{ minWidth: 'auto', px: 1 }}>
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>
          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={10}>
                <Button fullWidth size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)}>
                  {cm('cards.buttons.addNewCard', 'Add New Card')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button fullWidth variant="outlined" onClick={(e) => setAnchorElMore(e.currentTarget)} size="small" sx={{ minWidth: 'auto', px: 0.5 }}>
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Tablo */}
      <AccountingDataTable<any>
        columns={cardColumns}
        rows={cards}
        getRowId={(row) => row.id}
        loading={loading}
        pagination={false}
        defaultSortField="createdAt"
        defaultSortDirection="desc"
        emptyMessage={cm('common.noDataCards', 'No cards added yet.')}
        emptyIcon={<CreditCardIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={cm('cards.tableAriaLabel', 'card definitions table')}
        getRowSx={(row) => row.isMasterCard ? { backgroundColor: 'rgba(237, 108, 2, 0.08)' } : {}}
      />
      <Paper sx={{ borderRadius: 0, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, mt: -0.5 }}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={cm('common.rowsPerPage', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) => isEnglish ? `${from}–${to} of ${count}` : `${count} kayıttan ${from}–${to} arası`}
        />
      </Paper>

      {/* 3 Nokta Menüsü (Üst Toolbar) */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={() => setAnchorElMore(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setAnchorElMore(null); setBulkAddModalOpen(true); }}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.bulkAddCards', 'Bulk Add Cards')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); setPairingModalOpen(true); }}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.cardPairing', isEnglish ? 'Card Pairing' : 'Kart Eşleştirme')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); setCardTypeModalOpen(true); }}>
          <ListItemIcon><CategoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.cardTypes', 'Card Types')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); setWalletModalOpen(true); }}>
          <ListItemIcon><AccountBalanceWalletIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.wallets', 'Wallets')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); navigate('/kart-yonetimi/oturumlar'); }}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.cardSessions', 'Card Sessions')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); navigate('/kart-yonetimi/silinen-kartlar'); }}>
          <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.deletedCards', 'Deleted Cards')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorElMore(null); loadCards(); loadSummary(); }}>
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={cm('cards.buttons.refresh', 'Refresh')} />
        </MenuItem>
      </Menu>

      {/* Modals */}
      <AddCardModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setEditCardData(null); }}
        onSuccess={handleCardCreated}
        cardTypes={cardTypes}
        wallets={wallets}
        accounts={accounts}
        personnel={personnel}
        editCard={editCardData}
      />
      <BulkAddCardModal
        open={bulkAddModalOpen}
        onClose={() => setBulkAddModalOpen(false)}
        onSuccess={handleCardCreated}
        cardTypes={cardTypes}
        wallets={wallets}
        accounts={accounts}
      />
      <CardTypeModal open={cardTypeModalOpen} onClose={() => setCardTypeModalOpen(false)} companyId={companyId} onSuccess={loadMeta} />
      <WalletModal open={walletModalOpen} onClose={() => setWalletModalOpen(false)} companyId={companyId} onSuccess={loadMeta} />
      <CardPairingModal
        open={pairingModalOpen}
        onClose={() => setPairingModalOpen(false)}
        onSuccess={loadCards}
      />

      {/* Silme Onay Modalı */}
      <Dialog open={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDeleteTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{cm('cards.deleteDialog.title', 'Delete Card')}</DialogTitle>
        <DialogContent>
          {deleteCheckLoading ? (
            <Typography>{cm('cards.deleteDialog.checking', 'Checking...')}</Typography>
          ) : hasTransactions ? (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              {cm('cards.deleteDialog.hasTransactions', 'This card cannot be deleted because it has transaction records.')}
            </Alert>
          ) : (
            <Typography>
              {cm('cards.deleteDialog.question', 'Are you sure you want to delete card')} <strong>{deleteTarget?.cardNumber}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {hasTransactions || deleteCheckLoading ? (
            <Button onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}>{cm('common.close', 'Close')}</Button>
          ) : (
            <>
              <Button onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
              <Button onClick={handleDeleteCard} variant="contained" color="error" disabled={deleteLoading}>
                {deleteLoading ? cm('common.deleting', 'Deleting...') : cm('cards.actions.delete', 'Delete')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SessionDetailModal
        open={sessionDetailOpen}
        onClose={() => { setSessionDetailOpen(false); setSessionDetailTarget(null); }}
        session={sessionDetailTarget}
        companyId={companyId}
      />

      <Dialog
        open={closeSessionModalOpen}
        onClose={() => { setCloseSessionModalOpen(false); setCloseSessionTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {cm('cards.actions.closeSession', 'Close Session')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {cm('cards.closeSessionDialog.warning', 'The collected deposit may not have been refunded. Please be careful while closing the session. There may also be remaining balance on the card.')}
          </Alert>
          <Typography>
            {cm('cards.closeSessionDialog.question', 'Are you sure you want to close the active session for card')} <strong>{closeSessionTarget?.cardNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCloseSessionModalOpen(false); setCloseSessionTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleCloseSession} variant="contained" color="warning" disabled={closeSessionLoading}>
            {closeSessionLoading ? cm('cards.closeSessionDialog.closing', 'Closing...') : cm('cards.actions.closeSession', 'Close Session')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Oturumu Başlat Onay Modalı */}
      <Dialog
        open={openSessionModalOpen}
        onClose={() => { setOpenSessionModalOpen(false); setOpenSessionTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayCircleOutlineIcon color="success" />
          {cm('cards.actions.openSession', 'Start Session')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {cm('cards.openSessionDialog.question', 'Are you sure you want to start a session for card')} <strong>{openSessionTarget?.cardNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenSessionModalOpen(false); setOpenSessionTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleOpenSession} variant="contained" color="success" disabled={openSessionLoading}>
            {openSessionLoading ? cm('cards.openSessionDialog.opening', 'Starting...') : cm('cards.actions.openSession', 'Start Session')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kartı Teslim Al Onay Modalı */}
      <Dialog
        open={returnCardModalOpen}
        onClose={() => { setReturnCardModalOpen(false); setReturnCardTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentReturnIcon color="secondary" />
          {cm('cards.actions.returnCard', 'Return Card')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {cm('cards.returnCardDialog.info', 'The card will be returned and its session closed. If there is a deposit, it will be marked as settled without prompting for a refund (no accounting transaction).')}
          </Alert>
          <Typography>
            {cm('cards.returnCardDialog.question', 'Are you sure you want to return card')} <strong>{returnCardTarget?.cardNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReturnCardModalOpen(false); setReturnCardTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleReturnCard} variant="contained" color="secondary" disabled={returnCardLoading}>
            {returnCardLoading ? cm('cards.returnCardDialog.processing', 'Processing...') : cm('cards.actions.returnCard', 'Return Card')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geçiş Hakkı Tanımla Modalı */}
      <Dialog
        open={entryRightModalOpen}
        onClose={() => { setEntryRightModalOpen(false); setEntryRightTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MeetingRoomIcon color="primary" />
          {(entryRightTarget?.cardUID && entryRightsMap[entryRightTarget.cardUID])
            ? cm('cards.actions.editEntryRights', 'Edit Entry Rights')
            : cm('cards.actions.grantEntryRights', 'Define Entry Rights')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, mt: 1 }}>
            {cm('cards.entryRightDialog.card', 'Card')}: <strong>{entryRightTarget?.cardNumber}</strong>
          </Typography>
          {(() => {
            const current = entryRightTarget?.cardUID ? entryRightsMap[entryRightTarget.cardUID] : undefined;
            if (!current) return null;
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cm('cards.entryRightDialog.existing', 'This card already has defined entry rights. Saving will overwrite the current rights.')}
              </Alert>
            );
          })()}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{cm('cards.entryRightDialog.mode', 'Entry Mode')}</InputLabel>
            <Select
              value={entryRightMode}
              label={cm('cards.entryRightDialog.mode', 'Entry Mode')}
              onChange={(e) => setEntryRightMode(e.target.value)}
            >
              <MenuItem value="SINGLE_ENTRY">{cm('cards.entryRightDialog.singleEntry', 'Limited (count-based)')}</MenuItem>
              <MenuItem value="UNLIMITED_DAILY">{cm('cards.entryRightDialog.unlimitedDaily', 'Unlimited (daily)')}</MenuItem>
            </Select>
          </FormControl>
          {entryRightMode === 'SINGLE_ENTRY' && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label={cm('cards.entryRightDialog.count', 'Number of entries')}
              value={entryRightCount}
              onChange={(e) => setEntryRightCount(e.target.value)}
              inputProps={{ min: 1 }}
            />
          )}
          {entryRightMode === 'UNLIMITED_DAILY' && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {cm('cards.entryRightDialog.unlimitedHint', 'The card can enter without a count limit while the session is open.')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEntryRightModalOpen(false); setEntryRightTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleGrantEntryRights} variant="contained" color="primary" disabled={entryRightLoading}>
            {entryRightLoading ? cm('cards.entryRightDialog.saving', 'Saving...') : cm('cards.entryRightDialog.save', 'Define')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default Cards;
